import { useEffect, useState } from "react";
import GulfportHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const navItems = [
  { label: "Processing", href: "#processing" },
  { label: "Services", href: "#services" },
  { label: "Local Area", href: "#local-area" },
  { label: "Timing", href: "#timing" },
  { label: "Destinations", href: "#destinations" },
  { label: "Facilities", href: "#facilities" },
  { label: "Compare", href: "#compare" },
  { label: "FAQ", href: "#faq" },
  { label: "Resources", href: "#resources" },
];

const faqs = [
  { q: "How fast can I get a passport in Gulfport, MS?", a: "Our emergency service delivers passports in as little as 24 hours. Expedited processing takes 3–5 business days. For Keesler AFB personnel and military families, we handle even the most urgent requests." },
  { q: "Where is the nearest passport acceptance facility in Gulfport?", a: "The Gulfport Main Post Office on 28th Street is the primary facility. The Harrison County Chancery Clerk's Office also accepts applications. We direct you to whichever has the shortest current wait time." },
  { q: "Can Keesler Air Force Base airmen get expedited passports?", a: "Yes. We regularly assist Keesler AFB personnel with deployment orders, PCS moves, and family passports for overseas assignments. Our emergency service is specifically designed for military timing." },
  { q: "Do I need an appointment for a passport in Gulfport?", a: "Most Gulfport facilities are walk-in. We advise on optimal arrival times based on current wait times and day of week." },
  { q: "Can Gulf Coast casino workers get passports for cruise ship employment?", a: "Yes. We assist cruise ship employees with initial passport applications and urgent renewals — including those who need passports for employment onboard international vessels." },
  { q: "What documents are needed for a passport in Gulfport?", a: "You need a certified birth certificate with raised seal, valid government photo ID, compliant passport photo, and the appropriate form (DS-11 for new passports, DS-82 for eligible renewals). We verify all documents before your facility visit." },
  { q: "Is New Orleans closer than Jackson for emergency passport processing?", a: "The New Orleans Regional Passport Agency is about 1.5 hours west of Gulfport. However, our courier service eliminates the need to drive — we handle everything remotely with faster results." },
  { q: "How much does expedited passport service cost from Gulfport?", a: "Government fees are $130–$190. Our service fees start at $99. Emergency same-day service is available at higher tiers. Best price guarantee — we match any lower rate." },
];

const destinations = [
  { name: "Caribbean Cruises", country: "Various", flag: "🚢", desc: "New Orleans and Mobile cruise ports. Gulfport is one of the most convenient Coast cities for cruises." },
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Top direct-flight beach destination from the Gulf Coast. Popular for spring break." },
  { name: "Germany", country: "Germany", flag: "🇩🇪", desc: "Keesler AFB personnel frequently assigned to Ramstein Air Base and European installations." },
  { name: "Japan", country: "Japan", flag: "🇯🇵", desc: "Yokota and Misawa assignments for Keesler personnel. Kadena AFB assignments also common." },
  { name: "Costa Rica", country: "Costa Rica", flag: "🇨🇷", desc: "Gulf Coast ecotourism and adventure travel. Growing destination for coastal Mississippi families." },
  { name: "Bahamas", country: "Bahamas", flag: "🇧🇸", desc: "Popular weekend cruise destination from Mobile and New Orleans for Gulfport families." },
];

const facilities = [
  { name: "Gulfport Main Post Office", address: "2711 28th St, Gulfport, MS 39501", phone: "(228) 863-7931", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "15–35 min" },
  { name: "Harrison County Chancery Clerk", address: "1801 23rd Ave, Gulfport, MS 39501", phone: "(228) 865-4131", hours: "Mon–Fri 8:00 AM – 4:30 PM", wait: "10–25 min" },
  { name: "Biloxi Post Office", address: "135 Main St, Biloxi, MS 39530", phone: "(228) 374-4010", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "10–20 min" },
];

const testimonials = [
  { name: "Airman 1st Class Tyler M.", city: "Keesler AFB, Gulfport", stars: 5, text: "Emergency orders to Japan with 3 days notice. Fast Passport Center delivered my passport in 48 hours. Absolute lifesaver." },
  { name: "Maria G.", city: "Long Beach, MS", stars: 5, text: "Getting a cruise ship job requires a passport fast. They had mine processed in 4 days. Highly recommend for Gulf Coast job seekers." },
  { name: "The Harrison Family", city: "D'Iberville", stars: 5, text: "Caribbean cruise with the whole family — 5 passports needed. All done in 5 days. Incredible service." },
];

export default function GulfportPage() {
  useEffect(() => {
    document.title = "Emergency Passport Gulfport, MS — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    setLink("canonical", "https://fastpassportcenter.com/gulfport/");
    setMeta("description", "Emergency and expedited passport services in Gulfport, MS — as fast as 24 hours. Serving Keesler AFB military, Gulf Coast families, and cruise travelers. A+ BBB rated.");
    setMeta("geo.placename", "Gulfport, Mississippi");
    setMeta("geo.region", "US-MS");
    setMeta("geo.position", "30.3674;-89.0928");
    setMeta("ICBM", "30.3674, -89.0928");
    return () => { document.querySelector('link[rel="canonical"]')?.remove(); };
  }, []);

  return (
    <div className="font-sans">
      <GulfportHeroSection />
      <StickyNav />
      <ProcessingSection />
      <ServicesSection />
      <LocalAreaSection />
      <TravelTimingSection />
      <DestinationsSection />
      <TestimonialsSection />
      <AcceptanceFacilitiesSection />
      <ComparisonSection />
      <MistakesSection />
      <HowItWorksSection />
      <FAQSection />
      <HelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/gulfport" state="MS" />
      <FinalCTA />
    </div>
  );
}

function StickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = navItems.map((n) => document.querySelector(n.href));
      const scroll = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.getBoundingClientRect().top + window.scrollY <= scroll) { setActive(navItems[i].href); return; }
      }
      setActive("");
    };
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {navItems.map((item) => (<a key={item.href} href={item.href} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active === item.href ? "bg-[#1A365D] text-white" : "text-gray-600 hover:bg-gray-100"}`}>{item.label}</a>))}
        </div>
      </div>
    </div>
  );
}

function ProcessingSection() {
  return (
    <section id="processing" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How Fast Can You Get a Passport in Gulfport?</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">Military, civilian, or cruise traveler — we have a tier for every timeline.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $299", badge: "Fastest", desc: "For Keesler AFB orders and urgent Gulf Coast civilian travel needs.", features: ["24-hour turnaround available", "Military-compatible processing", "Real-time tracking", "Phone support 7 days/week"] },
            { title: "Expedited", time: "3–5 Business Days", price: "From $149", badge: "Most Popular", desc: "Ideal for cruise travelers, spring break families, and Gulf Coast professionals.", features: ["3–5 day guaranteed delivery", "Application review included", "Photo compliance check", "Email + phone support"] },
            { title: "Standard", time: "7–10 Business Days", price: "From $99", badge: "Best Value", desc: "Still dramatically faster than government standard. Great for planned travel.", features: ["7–10 day processing", "Complete application guidance", "Document checklist", "Email support"] },
          ].map((plan, i) => (
            <div key={i} className={`bg-white border rounded-xl p-7 flex flex-col ${plan.badge === "Most Popular" ? "border-[#F6C344] ring-1 ring-[#F6C344]" : "border-gray-200"}`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${plan.badge === "Fastest" ? "bg-red-100 text-red-700" : plan.badge === "Most Popular" ? "bg-[#F6C344]/20 text-[#1A365D]" : "bg-gray-100 text-gray-700"}`}>{plan.badge}</span>
                <span className="text-[#1A365D] font-bold text-lg">{plan.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-xl mb-1">{plan.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{plan.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">{plan.desc}</p>
              <ul className="space-y-2 mb-6">{plan.features.map((f, j) => <li key={j} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>{f}</li>)}</ul>
              <a href="https://fastpassportcenter.com/checkout/" className="block text-center bg-[#1A365D] hover:bg-[#0f2341] text-white font-semibold text-sm py-3 rounded-lg transition-all whitespace-nowrap">Get Started</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "New Passport", desc: "First-time applications for Gulf Coast residents." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for military and civilian travelers." },
    { icon: "ri-alert-line", title: "Lost/Stolen", desc: "Emergency replacement with DS-64 guidance." },
    { icon: "ri-user-smile-line", title: "Child Passport", desc: "Complete guidance for minors under 16." },
    { icon: "ri-time-line", title: "Emergency", desc: "24-hour processing for Keesler AFB and urgent civilian needs." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Post-marriage or legal name updates." },
  ];
  return (
    <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Gulfport &amp; the Gulf Coast</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className={`${s.icon} text-[#1A365D] text-xl`}></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalAreaSection() {
  return (
    <section id="local-area" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Gulfport, Mississippi</h2>
        </div>
        <div className="max-w-3xl mx-auto text-gray-600 text-base leading-relaxed space-y-5">
          <p>Gulfport, Mississippi's second-largest city and the heart of the Mississippi Gulf Coast, generates significant and diverse international passport demand. Situated along the Gulf of Mexico with Biloxi to the east and Long Beach to the west, the Gulfport-Biloxi metropolitan area serves a population of over 400,000 and functions as a military, tourism, healthcare, and gaming hub for the entire region.</p>
          <p>Keesler Air Force Base in adjacent Biloxi is the most significant single driver of passport demand on the Gulf Coast. Home to the 81st Training Wing and a major Air Force medical center, Keesler processes thousands of active duty personnel and their families annually for PCS moves, deployments, and TDY assignments worldwide. The 403rd Wing, an Air Force Reserve Command unit at Keesler, adds hundreds of reservists with international military travel needs. Military family members following personnel to overseas assignments also need passports often on very short timelines.</p>
          <p>The Gulf Coast tourism and gaming economy also generates substantial passport demand. Casino resort employees, particularly those in management roles with international parent companies, travel to Europe and Asia for training and conferences. The Port of Gulfport handles international cargo, with maritime professionals regularly requiring passports for vessel documentation and international port calls. Memorial Hospital and Garden Park Medical Center serve healthcare workers with medical conference and mission trip travel needs.</p>
          <p>Gulfport residents frequently use the New Orleans cruise port — just 90 minutes west — for Caribbean and Mexico cruises, and the Port of Mobile — just 90 minutes east — for additional cruise options. Spring break, summer vacation, and holiday cruise season all generate passport demand peaks that require faster processing than the standard government timeline provides. Our service ensures Gulf Coast families never miss their departure date due to a passport delay.</p>
        </div>
      </div>
    </section>
  );
}

function TravelTimingSection() {
  return (
    <section id="timing" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">When Gulfport Travelers Need Passports</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { month: "Year-Round", title: "Military Deployments", desc: "Keesler AFB personnel and families need passports on unpredictable military timelines year-round." },
            { month: "February–April", title: "Spring Break & Cruise Season", desc: "New Orleans and Mobile cruise departures peak. Cancun spring break flights surge." },
            { month: "May–June", title: "Summer Travel", desc: "Caribbean and beach vacations for Gulf Coast families. Casino employee training travel." },
            { month: "July–August", title: "Mission Trips", desc: "Church and hospital mission teams depart for Caribbean and Latin America." },
            { month: "September–October", title: "Off-Season Renewal", desc: "Ideal time to renew before the holiday cruise season." },
            { month: "November–December", title: "Holiday Cruises", desc: "New Orleans Christmas cruises and winter Caribbean departures. Rush orders spike." },
          ].map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-wider mb-2">{t.month}</p>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{t.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Gulfport Travelers Go</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.map((d, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{d.flag}</span>
                <div><h3 className="font-bold text-[#1A365D] text-lg">{d.name}</h3><p className="text-gray-400 text-xs">{d.country}</p></div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Gulfport Travelers Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-1 mb-3">{[...Array(t.stars)].map((_, j) => <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EEF2F8] flex items-center justify-center"><i className="ri-user-line text-[#1A365D]"></i></div>
                <div><p className="font-semibold text-[#1A365D] text-sm">{t.name}</p><p className="text-gray-400 text-xs">{t.city}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AcceptanceFacilitiesSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="facilities" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Acceptance Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Gulfport</h2>
        </div>
        <div className="space-y-4 max-w-3xl mx-auto">
          {facilities.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-map-pin-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><p className="text-gray-400 text-xs">{f.address}</p></div>
                </div>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl`}></i>
              </button>
              {open === i && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Phone</p><p className="text-[#1A365D] text-sm font-medium">{f.phone}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Hours</p><p className="text-[#1A365D] text-sm font-medium">{f.hours}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Avg Wait</p><p className="text-[#1A365D] text-sm font-medium">{f.wait}</p></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows = [
    { feature: "Processing Speed", us: "As fast as 24 hours", them: "10–13 weeks standard" },
    { feature: "Military Compatibility", us: "Full support for PCS and deployment orders", them: "No military-specific support" },
    { feature: "Office Visit Required", us: "No — mail or drop-off", them: "Yes — in-person required" },
    { feature: "Application Review", us: "Expert review before submission", them: "No review — errors cause delays" },
    { feature: "Tracking", us: "Real-time status updates", them: "Limited status info" },
    { feature: "Customer Support", us: "Phone + email, 7 days/week", them: "Phone only, limited hours" },
    { feature: "Price Guarantee", us: "Best price guarantee", them: "No price matching" },
    { feature: "Refund Policy", us: "Satisfaction guarantee", them: "No refunds after submission" },
  ];
  return (
    <section id="compare" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. DIY Government Processing</h2>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 border-b border-gray-200 bg-[#EEF2F8]">
            <div className="p-4 font-bold text-[#1A365D] text-sm">Feature</div>
            <div className="p-4 font-bold text-[#1A365D] text-sm text-center">Fast Passport Center</div>
            <div className="p-4 font-bold text-gray-500 text-sm text-center">Government Standard</div>
          </div>
          {rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 ${i !== rows.length - 1 ? "border-b border-gray-100" : ""}`}>
              <div className="p-4 text-sm text-gray-600 font-medium">{row.feature}</div>
              <div className="p-4 text-sm text-[#1A365D] font-semibold text-center bg-[#F7F8FA]/50">{row.us}</div>
              <div className="p-4 text-sm text-gray-500 text-center">{row.them}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MistakesSection() {
  const mistakes = [
    { title: "Military Timeline Mismatch", desc: "PCS and deployment orders arrive on short notice. Renew your passport before orders arrive — not after." },
    { title: "Cruise Boarding Day Surprise", desc: "Showing up at New Orleans or Mobile with an expired passport means missing your ship. We prevent this." },
    { title: "Wrong Photo", desc: "Photos from Gulf Coast CVS and Walgreens stores are commonly rejected. We verify photo specs in advance." },
    { title: "Late Application Start", desc: "Many Gulf Coast residents start passport processing 3 weeks before departure. We recommend starting 6–8 weeks early." },
    { title: "Form Confusion", desc: "DS-11 vs DS-82 is a common source of errors. We identify the correct form immediately." },
  ];
  return (
    <section id="mistakes" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Mistakes Gulfport Applicants Make</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-4"><i className="ri-close-circle-line text-red-500 text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{m.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { num: "01", title: "Choose Your Service", desc: "Emergency, expedited, or standard — matched to your departure date." },
    { num: "02", title: "Complete Application", desc: "DS-11 or DS-82 — we walk you through every field." },
    { num: "03", title: "Visit Acceptance Facility", desc: "We direct you to the Gulfport facility with the shortest wait time." },
    { num: "04", title: "We Handle Processing", desc: "Hand-carried to the State Department for priority processing." },
    { num: "05", title: "Receive Your Passport", desc: "Secure delivery with real-time tracking." },
  ];
  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">How It Works</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Steps to Your Passport</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-lg mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="py-16 md:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Gulfport, MS Passport Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl flex-shrink-0`}></i>
              </button>
              {open === i && <div className="px-5 pb-5 pt-0 border-t border-gray-100"><p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HelpfulResourcesSection() {
  return (
    <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Gulfport Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Travel.State.Gov", desc: "Official U.S. Department of State passport information.", link: "https://travel.state.gov/content/travel/en/passports.html" },
            { title: "Gulfport-Biloxi Airport", desc: "GPT airport flight information and international connections.", link: "https://www.flygpt.com/" },
            { title: "Keesler AFB", desc: "81st Training Wing military travel resources.", link: "https://www.keesler.af.mil/" },
          ].map((r, i) => (
            <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all group">
              <h3 className="font-bold text-[#1A365D] text-lg mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{r.desc}</p>
              <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-semibold group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-[#1A365D]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport in Gulfport?</h2>
        <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">Military, civilian, or cruise traveler — we have the speed and expertise to get your passport when you need it.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Passport <i className="ri-arrow-right-line"></i></a>
        <p className="text-white/50 text-sm mt-6">Call us at <a href="tel:8772530084" className="text-[#F6C344] hover:underline">877-253-0084</a> for immediate assistance</p>
      </div>
    </section>
  );
}